import React, {
  useState,
  useLayoutEffect,
  useRef,
  useEffect,
  useCallback
} from "react";
import PropTypes from "kf-proptypes/widgets";

import { generateId } from "base/utils";

import {
  FloatingMenu,
  FLOATING_MENU_POSITION
} from "../../dropdown/floatingmenu.jsx";
import { MenuItem } from "../../dropdown/menu.jsx";
import { UserIcon, AVATAR_SIZE } from "@widgets/datadisplay/index.jsx";

import styles from "./styles.css";

const KEY_CODES = {
  arrowUp: 38,
  arrowDown: 40,
  escape: 27,
  enter: 13
};

MentionListContainer.propTypes = {
  list: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      Kind: PropTypes.string.isRequired,
      Email: PropTypes.string,
      Name: PropTypes.string.isRequired,
      Role: PropTypes.string
    })
  ),
  iconRenderer: PropTypes.func,
  style: PropTypes.shape({
    display: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    left: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  className: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired
};

export function MentionListContainer({
  list,
  iconRenderer,
  style,
  className,
  onSelect,
  type
}) {
  const menuRef = useRef();
  const elementId = useRef(generateId("element"));
  const [selectedIndex, setSelectedIndex] = useState(0);

  const itemRenderer = useCallback(
    function menuItemRenderer(item, index) {
      if (type === "@") {
        return (
          <>
            {item.subHeadingName && (
              <div className={`${styles.subheading} small`}>
                {item.subHeadingName}
              </div>
            )}
            <MenuItem
              id={`floating-item-${index}`}
              key={index}
              className={`${styles.listcontainer} mentioncontainer`}
              tabIndex="-1"
              onMouseDown={(e) => e.preventDefault()}
            >
              {iconRenderer && iconRenderer(item)}
              {!iconRenderer && (
                <UserIcon
                  userId={item._id}
                  size={AVATAR_SIZE.SIZE36}
                  name={item.Name}
                />
              )}
              <div className={styles.usercontainer}>
                <div className={`${styles.userdetail} base`}>{item.Name}</div>
                {(item.Email || item.Description) && (
                  <div className={`${styles.useremail} tiny`}>
                    {item.Email || item.Description}
                  </div>
                )}
              </div>
            </MenuItem>
          </>
        );
      }
      return null;
    },
    [type, iconRenderer]
  );

  useEffect(function updateKeyHandler() {
    function keyDownHandler(e) {
      if (e.keyCode === KEY_CODES.arrowUp) {
        menuRef.current.previous();
      } else if (e.keyCode === KEY_CODES.arrowDown) {
        menuRef.current.next();
      } else if (e.keyCode === KEY_CODES.enter) {
        menuRef.current.select();
      } else if (e.keyCode === KEY_CODES.escape) {
        menuRef.current.hide();
      }
    }

    function onclickHandler(e) {
      menuRef.current.hide();
    }

    function onScrollHandler(e) {
      if (!e.target.closest(`.${elementId.current}`)) {
        menuRef.current.hide();
      }
    }

    menuRef.current.show();
    document.addEventListener("keydown", keyDownHandler);
    document.addEventListener("click", onclickHandler);
    document.body.addEventListener("wheel", onScrollHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
      document.removeEventListener("click", onclickHandler);
      document.body.removeEventListener("wheel", onScrollHandler);
    };
  }, []);

  useEffect(
    function updateList() {
      menuRef.current.show();
    },
    [list]
  );

  const scrollIntoItem = (selectedItem, selectedItemIndex) => {
    setSelectedIndex(selectedItemIndex);
  };

  useLayoutEffect(
    function updateScrollHandler() {
      var element = document.getElementById(`floating-item-${selectedIndex}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView(
            {
              block: "nearest",
              behavior: "smooth"
            },
            100
          );
        });
      }
    },
    [selectedIndex]
  );

  return (
    <FloatingMenu
      ref={menuRef}
      className={`${styles.container} ${className} ${elementId.current}`}
      dataSource={list}
      onSelect={(e) => onSelect(e)}
      top={style.top - 20}
      left={style.left}
      itemRenderer={itemRenderer}
      position={FLOATING_MENU_POSITION.BOTTOM_LEFT}
      onChange={scrollIntoItem}
    />
  );
}
